import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom-v5-compat";
import { useModuleBEM } from "@b2bportal/core-themes";
import { LazyLoading } from "@b2bportal/core-ui";
import { EligibleProduct } from "@b2bportal/wallet-offer-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import styles from "./styles.module.scss";
import { Experiments, useExperiment } from "@/modules/experiments/src";
import {
  PLACEHOLDER_DESTINATIONS,
  WalletOfferCard,
} from "@/pages/Homepage/WalletOffersSection";
import { OfferRawEventName, useEmitRbcRawEvent } from "@/tracking/offers";
import { useWalletOffers } from "@/utilities";

/** Section anchors: /travel-offers#offers and /travel-offers#credits. */
export const TRAVEL_OFFERS_SECTION_IDS = {
  offers: "offers",
  credits: "credits",
} as const;

export const TravelOffersPage = () => {
  const [block, cn] = useModuleBEM(styles, "TravelOffersPage");
  const { t } = useI18nContext();
  const { hash } = useLocation();

  const { isExperimentVariant, data: experimentsData } = useExperiment();
  const walletOffersEnabled = isExperimentVariant(
    Experiments.WALLET_OFFERS.name,
    Experiments.WALLET_OFFERS.variants.available
  );

  const { offers, isLoading } = useWalletOffers({
    skip: !walletOffersEnabled,
  });

  const emitViewedAllOffers = useEmitRbcRawEvent(
    OfferRawEventName.ViewedAllOffers
  );
  const firedRef = useRef(false);
  useEffect(() => {
    if (!isLoading && !firedRef.current) {
      firedRef.current = true;
      // TODO(RBC-2641): detect entry_source from navigation/URL params
      emitViewedAllOffers({
        properties: {
          entry_source: "other",
          offers_count: offers.length,
          claimed_offers_count: 0, // wallet-offer-api doesn't distinguish claimed
          offers_list: offers.map((o) => o.id),
        },
      });
    }
  }, [isLoading, offers, emitViewedAllOffers]);

  // react-router doesn't scroll to fragments; bring the linked section into
  // view once offers have rendered.
  useEffect(() => {
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    }
  }, [hash, offers.length]);

  // Don't redirect while experiments are unresolved (direct page loads render
  // before the query returns; isExperimentVariant is false until then).
  if (experimentsData == null) {
    return <LazyLoading />;
  }
  if (!walletOffersEnabled) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className={block}>
      <div className={cn("header")}>
        <h1 className={cn("title")}>{t("travelWalletPage.title")}</h1>
        <p className={cn("subtitle")}>{t("travelWalletPage.subtitle")}</p>
      </div>
      <div className={cn("divider")} />
      <section id={TRAVEL_OFFERS_SECTION_IDS.offers} className={cn("section")}>
        <h2 className={cn("section-title")}>
          {t("travelWalletPage.offersSectionTitle", { count: offers.length })}
        </h2>
        <div className={cn("grid")}>
          {offers.map((offer, index) => (
            <WalletOfferCard
              key={offer.id}
              offer={offer}
              product={offer.eligibleProducts?.[0] ?? EligibleProduct.Lodging}
              destination={
                PLACEHOLDER_DESTINATIONS[
                  index % PLACEHOLDER_DESTINATIONS.length
                ]
              }
            />
          ))}
        </div>
      </section>
    </div>
  );
};
